import { BrandCampaign, BrandCampaignPatchDTO, CentralCampaignType } from '@retail/campaign/types';
import { useCampaignTFunction } from '@retail/campaign/i18n';
import {
  useDistributeCampaign,
  useEditBrandCampaign,
  useEditCampaignBrands,
} from '@retail/campaign/data-access';
import { useToast } from '@retail/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import { Brand } from '@retail/auth/types';
import { useInvalidateCampaignQueries } from './useInvalidateCampaignQueries';

interface Props {
  campaign: BrandCampaign;
  availableBrands: Brand[];
}

export const useBrandCampaignDetailsActions = ({ campaign, availableBrands }: Props) => {
  const toast = useToast();
  const t = useCampaignTFunction();
  const queryClient = useQueryClient();
  const invalidateCampaignQueries = useInvalidateCampaignQueries({ campaignId: campaign.id });

  const { mutateAsync: mutateEditCampaignAsync, isPending: isLoadingEdit } = useEditBrandCampaign({
    campaignId: campaign.id,
  });

  const { mutateAsync: mutateEditBrands, isPending: isLoadingBrands } = useEditCampaignBrands({
    campaignId: campaign.id,
  });

  const { mutateAsync: mutateDistributeCampaign, isPending: isLoadingDistribution } =
    useDistributeCampaign({
      campaignId: campaign.id,
    });

  const campaignPatchDtoBase: BrandCampaignPatchDTO = useMemo(
    () => ({
      campaignName: campaign.name,
      mandatory: campaign.mandatory,
      description: campaign.description,
      validTo: dayjs(campaign.validTo).format('YYYY-MM-DD'),
      validFrom: dayjs(campaign.validFrom).format('YYYY-MM-DD'),
      purchasePeriodTo: dayjs(campaign.purchasePeriodTo).format('YYYY-MM-DD'),
      purchasePeriodFrom: dayjs(campaign.purchasePeriodFrom).format('YYYY-MM-DD'),
      campaignType: campaign.campaignType as CentralCampaignType,
    }),
    [campaign]
  );

  const handleChangeCampaign = useCallback(
    async (campaignPatch: BrandCampaignPatchDTO) => {
      await mutateEditCampaignAsync({
        body: campaignPatch,
      })
        .then(() => {
          toast.success(t('campaign.messages.editCampaign'));
          queryClient.invalidateQueries({ queryKey: ['campaigns', campaign.id] });
        })
        .catch((error) => {
          console.error(error);
          toast.error(t('campaign.error.genericError'));
          throw error;
        });
    },
    [campaign.id, mutateEditCampaignAsync, queryClient, t, toast]
  );

  const handleChangeBrands = useCallback(
    (brandIds: string[]) => {
      const previousBrands = campaign.brands;
      campaign.brands = availableBrands.filter((brand) => brandIds.includes(brand.brandId));
      mutateEditBrands({ body: { brandIds } })
        .then(() => {
          toast.success(t('campaign.messages.editCampaign'));
          queryClient.invalidateQueries({ queryKey: ['campaigns', campaign.id] });
        })
        .catch((error) => {
          console.error(error);
          toast.error(t('campaign.error.genericError'));
          campaign.brands = previousBrands;
        });
    },
    [availableBrands, campaign, mutateEditBrands, queryClient, t, toast]
  );

  const handleChangeCampaignName = useCallback(
    async (name: string) => {
      const previousName = campaign.name;
      campaign.name = name;
      await handleChangeCampaign({ ...campaignPatchDtoBase, campaignName: name }).catch(() => {
        campaign.name = previousName;
      });
    },
    [campaign, campaignPatchDtoBase, handleChangeCampaign]
  );

  const handleChangeDescription = useCallback(
    async (description: string) => {
      const previousDescription = campaign.description;
      campaign.description = description;
      await handleChangeCampaign({ ...campaignPatchDtoBase, description }).catch(() => {
        campaign.description = previousDescription;
      });
    },
    [campaign, campaignPatchDtoBase, handleChangeCampaign]
  );

  const handleChangeCampaignPeriod = useCallback(
    async ({ validTo, validFrom }: Pick<BrandCampaign, 'validTo' | 'validFrom'>) => {
      const previousValidFrom = campaign.validFrom;
      const previousValidTo = campaign.validTo;
      campaign.validFrom = validFrom;
      campaign.validTo = validTo;

      await handleChangeCampaign({
        ...campaignPatchDtoBase,
        validTo: dayjs(validTo).format('YYYY-MM-DD'),
        validFrom: dayjs(validFrom).format('YYYY-MM-DD'),
      }).catch(() => {
        campaign.validTo = previousValidTo;
        campaign.validFrom = previousValidFrom;
      });
    },
    [campaign, campaignPatchDtoBase, handleChangeCampaign]
  );

  const handleChangePurchasePeriod = useCallback(
    async ({
      purchasePeriodTo,
      purchasePeriodFrom,
    }: Pick<BrandCampaign, 'purchasePeriodTo' | 'purchasePeriodFrom'>) => {
      const previousPurchaseFrom = campaign.purchasePeriodFrom;
      const previousPurchaseTo = campaign.purchasePeriodTo;
      campaign.purchasePeriodFrom = purchasePeriodFrom;
      campaign.purchasePeriodTo = purchasePeriodTo;

      await handleChangeCampaign({
        ...campaignPatchDtoBase,
        purchasePeriodTo: dayjs(purchasePeriodTo).format('YYYY-MM-DD'),
        purchasePeriodFrom: dayjs(purchasePeriodFrom).format('YYYY-MM-DD'),
      }).catch(() => {
        campaign.purchasePeriodTo = previousPurchaseTo;
        campaign.purchasePeriodFrom = previousPurchaseFrom;
      });
    },
    [campaign, campaignPatchDtoBase, handleChangeCampaign]
  );

  const handleChangeMandatory = useCallback(
    async (mandatory: boolean) => {
      const previousMandatory = campaign.mandatory;
      campaign.mandatory = mandatory;
      await handleChangeCampaign({ ...campaignPatchDtoBase, mandatory }).catch(() => {
        campaign.mandatory = previousMandatory;
      });
    },
    [campaign, campaignPatchDtoBase, handleChangeCampaign]
  );

  const handleChangeCampaignType = useCallback(
    (campaignType: CentralCampaignType) => {
      const previousCampaignType = campaign.campaignType;
      campaign.campaignType = campaignType;
      handleChangeCampaign({ ...campaignPatchDtoBase, campaignType }).catch(() => {
        campaign.campaignType = previousCampaignType;
      });
    },
    [campaign, campaignPatchDtoBase, handleChangeCampaign]
  );

  const handleCampaignDistribution = useCallback(async () => {
    await mutateDistributeCampaign({ urlVariables: campaign.id });
    toast.success(t('campaign.messages.distributed'));
    invalidateCampaignQueries();
  }, [campaign.id, invalidateCampaignQueries, mutateDistributeCampaign, t, toast]);

  return useMemo(
    () => ({
      isLoadingEdit,
      isLoadingDistribution,
      isLoadingBrands,
      handleChangeCampaignName,
      handleChangeDescription,
      handleChangeCampaignPeriod,
      handleChangePurchasePeriod,
      handleChangeMandatory,
      handleChangeBrands,
      handleChangeCampaignType,
      handleCampaignDistribution,
    }),
    [
      isLoadingEdit,
      isLoadingDistribution,
      isLoadingBrands,
      handleChangeCampaignName,
      handleChangeDescription,
      handleChangeCampaignPeriod,
      handleChangePurchasePeriod,
      handleChangeMandatory,
      handleChangeBrands,
      handleChangeCampaignType,
      handleCampaignDistribution,
    ]
  );
};
